import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense, useCallback, useEffect, useRef, useState} from 'react'
import {ForYouSubSection} from './shared/ForYouSubSection'
import {
  getExpandedSubSections,
  isForYouEmpty,
  type ForYouSubSectionId,
  type ForYouSubSectionState,
  type ForYouSubSectionVisibility,
} from './shared/pick-expanded-section'
import {RequiredReviewer} from './mergeability/RequiredReviewer'
import {ApprovableChecks} from './mergeability/ApprovableChecks'
import {AwaitingApprovalChecks} from './mergeability/AwaitingApprovalChecks'
import {MergeConflicts} from './mergeability/MergeConflicts'
import {FailingChecks} from './mergeability/FailingChecks'
import {DraftPR} from './pull-request-state/DraftPR'
import {ReadyToMerge} from './mergeability/ReadyToMerge'
import {ReviewSubmitted} from './mergeability/ReviewSubmitted'
import {AbleToMerge} from './mergeability/AbleToMerge'
import {Findings} from './findings/Findings'
import {Deployments} from './deployments/Deployments'
import {ReviewRequested} from './reviewer/ReviewRequested'
import {ReviewPending} from './reviewer/ReviewPending'
import {ChangesSinceLastReview} from './reviewer/ChangesSinceLastReview'
import {ForYouSectionError, ForYouSectionSkeleton} from './ForYouSectionFallbacks'
import {ForYouItemBoundary} from './shared/ForYouItemBoundary'
import {MergeBlocked} from './mergeability/MergeBlocked'
import {DeleteHeadBranch} from './pull-request-state/DeleteHeadBranch'
import {PendingBatch} from './batch/PendingBatch'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'
import {ForYouEmptyState} from './ForYouEmptyState'
import {ClientOnly} from '../../ClientOnly'
import {MiniOverviewMergeStatus} from './mergeability/MiniOverviewMergeStatus'
import {isMiniMergeStatusEnabled} from '../mergebox/is-mini-merge-status-enabled'
import {ForYouNextAction, ForYouNextActionBoundary} from './ForYouNextAction'
import styles from './shared/ForYouSubSection.module.css'

const EMPTY_SUBSECTION_STATE: ForYouSubSectionState = {
  hasVisibleItems: false,
  hasLoadingItems: false,
  visibleItemCount: 0,
}

const DEFAULT_SUBSECTION_VISIBILITY: Partial<Record<ForYouSubSectionId, boolean>> = {
  comments: true,
}

export function ForYouSection() {
  if (isMiniMergeStatusEnabled()) {
    return <CondensedForYouSection />
  }

  return (
    <ErrorBoundary boundaryName="ForYouSection" fallback={<ForYouSectionError />}>
      <Suspense fallback={<ForYouSectionSkeleton />}>
        <ForYouSectionContent />
      </Suspense>
    </ErrorBoundary>
  )
}

function CondensedForYouSection() {
  return (
    <ErrorBoundary boundaryName="CondensedForYouSection" fallback={<ForYouSectionError />}>
      <Suspense fallback={null}>
        <div className={styles.CondensedForYouContainer}>
          <ForYouNextActionBoundary>
            <ForYouNextAction />
          </ForYouNextActionBoundary>
          <MiniOverviewMergeStatus />
        </div>
      </Suspense>
    </ErrorBoundary>
  )
}

function ForYouSectionContent() {
  // Which subsections currently have confirmed visible items and/or loading
  // placeholders. Fed by each subsection's `onVisibilityChange`.
  const [visibility, setVisibility] = useState<ForYouSubSectionVisibility>({
    needsAttention: EMPTY_SUBSECTION_STATE,
    relevant: EMPTY_SUBSECTION_STATE,
    comments: EMPTY_SUBSECTION_STATE,
  })

  // Sections the user has explicitly toggled; these always win over the priority recommendation.
  // Comments are always expanded by default, so we start with that override.
  const [userOverrides, setUserOverrides] =
    useState<Partial<Record<ForYouSubSectionId, boolean>>>(DEFAULT_SUBSECTION_VISIBILITY)

  const expanded = getExpandedSubSections(visibility, userOverrides)

  const miniMergeStatusEnabled = isMiniMergeStatusEnabled()
  const showEmptyState = isForYouEmpty(visibility) && !miniMergeStatusEnabled

  const handleToggle = useCallback(
    (id: ForYouSubSectionId, currentlyExpanded: boolean) =>
      setUserOverrides(prev => ({...prev, [id]: !currentlyExpanded})),
    [],
  )

  const handleVisibilityChange = useCallback(
    (id: ForYouSubSectionId, state: ForYouSubSectionState) =>
      setVisibility(prev => updateSubSectionVisibility(prev, id, state)),
    [],
  )

  return (
    <>
      <ForYouItemsPresentedReporter visibility={visibility} />
      <div className="d-flex gap-3 flex-column">
        <NeedsAttentionSection
          expanded={expanded.needsAttention}
          onToggle={handleToggle}
          onVisibilityChange={handleVisibilityChange}
        />
        <RelevantToYouSection
          expanded={expanded.relevant}
          onToggle={handleToggle}
          onVisibilityChange={handleVisibilityChange}
        />
        {showEmptyState && (
          <ClientOnly>
            <ForYouEmptyState />
          </ClientOnly>
        )}
      </div>
    </>
  )
}

/**
 * Fires `overview.for_you_items_presented` exactly once per For You mount, after the
 * initial load settles (no subsection still reports loading placeholders), reporting the
 * total number of visible items across every subsection.
 */
export function ForYouItemsPresentedReporter({visibility}: {visibility: ForYouSubSectionVisibility}) {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const hasFiredRef = useRef(false)

  const states = Object.values(visibility)
  const stillLoading = states.some(state => state.hasLoadingItems)
  const visibleItemCount = states.reduce((total, state) => total + state.visibleItemCount, 0)

  useEffect(() => {
    if (hasFiredRef.current || stillLoading) return

    const timeoutId = setTimeout(() => {
      if (hasFiredRef.current) return
      hasFiredRef.current = true
      sendPullRequestAnalyticsEvent('overview.for_you_items_presented', 'OVERVIEW_FOR_YOU_SECTION', {
        visible_item_count: visibleItemCount,
      })
    }, 0)

    return () => clearTimeout(timeoutId)
  }, [stillLoading, visibleItemCount, sendPullRequestAnalyticsEvent])

  return null
}

function updateSubSectionVisibility(
  visibility: ForYouSubSectionVisibility,
  section: ForYouSubSectionId,
  nextState: ForYouSubSectionState,
) {
  const currentState = visibility[section]
  if (
    currentState.hasVisibleItems === nextState.hasVisibleItems &&
    currentState.hasLoadingItems === nextState.hasLoadingItems &&
    currentState.visibleItemCount === nextState.visibleItemCount
  ) {
    return visibility
  }

  return {...visibility, [section]: nextState}
}

interface SubSectionProps {
  expanded: boolean
  onToggle: (id: ForYouSubSectionId, currentlyExpanded: boolean) => void
  onVisibilityChange: (id: ForYouSubSectionId, state: ForYouSubSectionState) => void
}

function NeedsAttentionSection({expanded, onToggle, onVisibilityChange}: SubSectionProps) {
  return (
    <ForYouSubSection
      id="needsAttention"
      title="Needs attention"
      expanded={expanded}
      onToggle={onToggle}
      onVisibilityChange={onVisibilityChange}
    >
      <ForYouItemBoundary name="ReviewPending">
        <ReviewPending />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="PendingBatch">
        <PendingBatch />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ReviewRequested">
        <ReviewRequested />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="MergeConflicts">
        <MergeConflicts />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="FailingChecks" audience="author">
        <FailingChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ApprovableChecks">
        <ApprovableChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ReviewSubmitted" audience="author">
        <ReviewSubmitted />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="MergeBlocked" audience="author">
        <MergeBlocked />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="DeleteHeadBranch" audience="author">
        <DeleteHeadBranch />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="Findings">
        <Findings />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="DraftPR" audience="author">
        <DraftPR />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ReadyToMerge" audience="author">
        <ReadyToMerge />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="AbleToMerge" audience="author">
        <AbleToMerge />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="ChangesSinceLastReview">
        <ChangesSinceLastReview />
      </ForYouItemBoundary>
    </ForYouSubSection>
  )
}

function RelevantToYouSection({expanded, onToggle, onVisibilityChange}: SubSectionProps) {
  return (
    <ForYouSubSection
      id="relevant"
      title="Relevant"
      expanded={expanded}
      onToggle={onToggle}
      onVisibilityChange={onVisibilityChange}
    >
      <ForYouItemBoundary name="Checks">
        <AwaitingApprovalChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="RequiredReviewer">
        <RequiredReviewer />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="FailingChecks" audience="reviewer">
        <FailingChecks />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="MergeBlocked" audience="reviewer">
        <MergeBlocked />
      </ForYouItemBoundary>

      <ForYouItemBoundary name="Deployments">
        <Deployments />
      </ForYouItemBoundary>
    </ForYouSubSection>
  )
}
